// Contenedor visual compartido para las pantallas públicas del panel
// (aceptar invitación, restablecer / recuperar contraseña). Sigue el tema
// de TiendaLogin.
export default function AuthShell({ title, subtitle, children, badge }) {
  return (
    <div style={s.page}>
      <div style={s.card}>
        {badge && <div style={s.badge}>{badge}</div>}
        <h2 style={s.title}>{title}</h2>
        {subtitle && <p style={s.sub}>{subtitle}</p>}
        <div style={{ marginTop: 18 }}>{children}</div>
        <div style={s.footerMark}>Cyravi by Nexus Bridge</div>
      </div>
      <style>{keyframes}</style>
    </div>
  );
}

const keyframes = `
@keyframes cyraviAdminIn {
  from { opacity: 0; transform: translateY(14px) scale(0.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}`;

const s = {
  page: {
    minHeight: "100dvh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    background: "#f7f9ff",
    boxSizing: "border-box",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 470,
    padding: "34px 30px",
    borderRadius: 24,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(95, 108, 133, 0.14)",
    boxShadow: "0 24px 70px rgba(74, 85, 120, 0.14)",
    backdropFilter: "blur(14px)",
    boxSizing: "border-box",
    animation: "cyraviAdminIn 0.5s ease",
    textAlign: "center",
  },
  logoWrap: {
    width: 56,
    height: 56,
    margin: "0 auto 16px",
    borderRadius: 18,
    background: "linear-gradient(135deg, rgba(109,94,252,0.12), rgba(77,163,255,0.12))",
    border: "1px solid rgba(109, 94, 252, 0.20)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoText: {
    fontSize: 20,
    fontWeight: 900,
    background: "linear-gradient(135deg, #6d5efc, #4da3ff)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    letterSpacing: "-0.04em",
  },
  badge: {
    display: "inline-block",
    padding: "6px 14px",
    borderRadius: 999,
    border: "1px solid rgba(109, 94, 252, 0.20)",
    background: "rgba(255,255,255,0.82)",
    color: "#4f46e5",
    fontSize: 11.5,
    fontWeight: 800,
    letterSpacing: "0.04em",
    marginBottom: 12,
  },
  title: {
    margin: 0,
    fontSize: 26,
    fontWeight: 900,
    letterSpacing: "-0.03em",
    color: "#182033",
    lineHeight: 1.15,
  },
  sub: {
    margin: "10px 0 0",
    fontSize: 14,
    fontWeight: 500,
    color: "#5f6b85",
    lineHeight: 1.55,
  },
  footerMark: {
    marginTop: 22,
    paddingTop: 14,
    borderTop: "1px solid rgba(95,108,133,0.10)",
    fontSize: 12,
    fontWeight: 900,
    color: "#4f46e5",
    letterSpacing: "0.04em",
  },
};

// Alerta formal y sobria (fondo blanco, barra de acento e ícono). Sin librerías.
export function Alert({ type = "error", children }) {
  const palette =
    type === "ok"
      ? { bar: "#16a34a", icon: "#16a34a", text: "#1f3d2b" }
      : type === "info"
      ? { bar: "#4f46e5", icon: "#4f46e5", text: "#28324d" }
      : { bar: "#dc2626", icon: "#dc2626", text: "#3f2222" };

  const icon =
    type === "ok" ? (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    ) : (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5" />
        <path d="M12 16h.01" />
      </svg>
    );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        textAlign: "left",
        background: "#ffffff",
        border: "1px solid #e6e8ef",
        borderLeft: `3px solid ${palette.bar}`,
        borderRadius: 10,
        padding: "11px 13px",
        marginBottom: 16,
        boxShadow: "0 4px 14px rgba(74, 85, 120, 0.06)",
      }}
    >
      <span style={{ color: palette.icon, flex: "0 0 auto", marginTop: 1, display: "flex" }}>{icon}</span>
      <span style={{ fontSize: 13.5, lineHeight: 1.45, fontWeight: 600, color: palette.text }}>{children}</span>
    </div>
  );
}

// Estilos reutilizables para botones/alertas dentro del shell.
export const authStyles = {
  primaryBtn: {
    width: "100%",
    padding: 14,
    marginTop: 6,
    border: "none",
    borderRadius: 14,
    cursor: "pointer",
    fontWeight: 900,
    color: "#fff",
    fontSize: 14.5,
    background: "linear-gradient(135deg, #6d5efc, #4da3ff)",
    boxShadow: "0 16px 30px rgba(109,94,252,0.18)",
  },
  alert: {
    padding: "12px 13px",
    borderRadius: 14,
    marginBottom: 14,
    fontSize: 13,
    fontWeight: 700,
    textAlign: "left",
  },
  alertError: { background: "#fff1f2", color: "#be123c", border: "1px solid #fecdd3" },
  alertOk: { background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0" },
  field: { display: "flex", flexDirection: "column", marginBottom: 14, textAlign: "left" },
  label: { fontSize: 13, fontWeight: 700, color: "#5f6b85", marginBottom: 8 },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "13px 14px",
    borderRadius: 14,
    border: "1px solid rgba(109, 94, 252, 0.18)",
    background: "#fbfcff",
    color: "#182033",
    fontSize: 14,
    outline: "none",
  },
  readonlyBox: {
    textAlign: "left",
    background: "#f3f4fb",
    border: "1px solid #e6e7f2",
    borderRadius: 12,
    padding: "12px 14px",
    marginBottom: 16,
    fontSize: 13.5,
    color: "#374151",
  },
  link: {
    background: "none",
    border: "none",
    color: "#4f46e5",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    textDecoration: "underline",
  },
};
